import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Banner, Button, Card, OtpInput } from '@cowoo/shared/ui-web';
import {
  OTP_RESEND_COOLDOWN_MS,
  authErrorKey,
  mapAuthError,
  handleAuthResult,
  otpSchema,
  type AuthErrorKind,
} from '@cowoo/shared';
import { auth } from '../../lib/auth';
import { afterAuthentication } from './afterAuth';
import { readPendingLogin, rememberDetour, rememberOtpMethod } from './pending';
import { useResendCooldown } from './useResendCooldown';

/** § 5.2 — after five wrong codes the old one is spent; ask for a new one. */
const MAX_ATTEMPTS = 5;

/**
 * `/login/code` (09-web-member § 4.1).
 *
 * This screen only ever verifies the e-mail code. Both detours of 04 § 11.2
 * have their own routes, so each one keeps the copy that explains what it is
 * asking for — a six-digit box with no explanation is how a user ends up
 * typing the e-mail code into the authenticator prompt.
 */
export function LoginCode() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pending = readPendingLogin();

  const [code, setCode] = useState('');
  const [error, setError] = useState<AuthErrorKind | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const resend = useResendCooldown();
  /**
   * Set the moment a session exists. `afterAuthentication` clears the pending
   * login, which re-renders this screen with nothing to verify against — and
   * the guard below would then send a user who just signed in back to
   * `/login`, racing the navigation that is already on its way.
   */
  const succeeded = useRef(false);

  // § 4.1 — opening this route without a pending e-mail (a cold tab, a reload
  // after the tab was closed) has nothing to verify against.
  useEffect(() => {
    if (!pending && !succeeded.current) navigate('/login', { replace: true });
  }, [pending, navigate]);

  async function verify(entered: string): Promise<void> {
    if (!pending || verifying) return;
    if (!otpSchema.safeParse(entered).success) {
      setError('otp_invalid');
      return;
    }

    setVerifying(true);
    setError(null);
    try {
      const state = handleAuthResult(await auth.verifyOtp(pending.methodId, entered));
      if (state.status === 'totpRequired') {
        rememberDetour({ kind: 'totp_required', token: state.token });
        navigate('/login/totp');
        return;
      }
      if (state.status === 'challengeRequired') {
        rememberDetour({ kind: 'challenge', token: state.token });
        navigate('/login/challenge');
        return;
      }
      if (state.status === 'authenticated') {
        succeeded.current = true;
        navigate(await afterAuthentication(state), { replace: true });
      }
    } catch (thrown) {
      const next = attempts + 1;
      setAttempts(next);
      // The code is spent after five tries, so say the thing that is now true
      // rather than repeating "wrong code" at a code that can no longer work.
      setError(next >= MAX_ATTEMPTS ? 'otp_expired' : mapAuthError(thrown, 'otp'));
      setCode('');
    } finally {
      setVerifying(false);
    }
  }

  async function sendAgain(): Promise<void> {
    if (!pending || !resend.ready) return;
    setError(null);
    try {
      const { methodId } = await auth.sendOtp(pending.email);
      rememberOtpMethod(pending.email, methodId);
      setAttempts(0);
      setCode('');
      resend.restart();
    } catch (thrown) {
      const kind = mapAuthError(thrown, 'otp');
      setError(kind);
      // OtterAuth's own limit is five per address per ten minutes; a 30 s
      // cooldown would only walk the user into the same wall again.
      if (kind === 'otp_rate_limited') resend.block();
    }
  }

  const key = error ? authErrorKey(error) : null;
  const spent = attempts >= MAX_ATTEMPTS || error === 'otp_expired';

  return (
    <main className="mx-auto max-w-md px-5 py-20">
      <Link to="/login" className="font-body text-muted mb-6 inline-block text-sm underline">
        {t('member.otp.otherEmail')}
      </Link>
      <h1 className="font-heading text-ink mb-2 text-3xl">{t('member.otp.title')}</h1>
      <p className="font-body text-text mb-6">{t('member.otp.body', { email: pending?.email ?? '' })}</p>
      <Card>
        <div className="flex flex-col gap-4">
          <OtpInput
            label={t('member.otp.code')}
            value={code}
            onChange={setCode}
            onComplete={(entered) => void verify(entered)}
            error={key ? t(key) : null}
            disabled={verifying}
          />
          <Button
            onClick={() => (spent ? void sendAgain() : void verify(code))}
            loading={verifying}
            disabled={!spent && code.length < 6}
            full
          >
            {spent ? t('member.otp.resend') : t('member.otp.submit')}
          </Button>

          <div className="font-body text-muted text-sm">
            {resend.justSent ? (
              <span className="rounded-pill bg-lime-tint text-lime-ink px-3 py-1">
                {t('member.otp.resent')}
              </span>
            ) : resend.ready ? (
              <button type="button" onClick={() => void sendAgain()} className="underline">
                {t('member.otp.resend')}
              </button>
            ) : (
              t('member.otp.resendIn', { seconds: resend.seconds })
            )}
          </div>

          <Banner tone="info">{t('member.otp.spam')}</Banner>
        </div>
      </Card>
    </main>
  );
}

export { OTP_RESEND_COOLDOWN_MS };
